import { z } from "zod/v4"
import { CreatedAt, UpdatedAt } from "./system"
import { SpaceBackgroundId, SpaceBGMIdSchema } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { DocumentStatus, MediaImage } from "../common"
import { AssetBundleFile } from "./item/file"

export const ManageCreateSpaceBackgroundRequest = z.object({
  name: z.string(),
  atmosphereText: z.string(),
  priority: z.int(),
  capacity: z.int().min(2).max(8),
  recommendBGM: SpaceBGMIdSchema.optional(),
})
export type ManageCreateSpaceBackgroundRequest = z.infer<typeof ManageCreateSpaceBackgroundRequest>

export const ManageUpdateSpaceBackgroundRequest = z.object({
  name: z.string().optional(),
  atmosphereText: z.string().optional(),
  priority: z.coerce.number<number>().int().optional(),
  capacity: z.coerce.number<number>().int().min(2).max(8).optional(),
  recommendBGM: SpaceBGMIdSchema.optional(),
  updateThumbnail: z.boolean().optional(),
  homeImageLocations: z.array(z.object({ value: z.string() })).max(50).optional(),
  status: DocumentStatus.optional(),
  androidAssetBundleName: z.string().optional(),
  iosAssetBundleName: z.string().optional(),
  linuxAssetBundleName: z.string().optional(),
})
export type ManageUpdateSpaceBackgroundRequest = z.infer<typeof ManageUpdateSpaceBackgroundRequest>

/**
 * この定数で定義されているプロパティは、items.ts の BGM (共通ではない、購入・プレゼントのBGM) と共通の項目
 */
export const SpaceBackgroundCommon = z.object({
  name: z.string().describe("表示名"),
  atmosphere_text: z.string().describe("天気"),
  capacity: z.int().min(2).max(8).describe("推奨人数"),
  recommend_bgm: SpaceBGMIdSchema.optional().nullable().describe("おすすめBGM設定"),
  thumbnail_url: z.url().describe("所有アイテム画像"),
  images: z.object({
    home: z.array(MediaImage.describe("ホーム背景画像。")).min(1).max(50),
  }),
  can_delete: z.boolean(),
  status: DocumentStatus,
})

/**
 * この定数で定義されているプロパティは、items.ts の BGM (共通ではない、購入・プレゼントのBGM) と共通"ではない"項目
 * つまり、アイテムと共通背景で異なる項目。
 */
export const SpaceBackgroundDocumentValue = z.intersection(
  SpaceBackgroundCommon,
  z.object({
    asset_bundle: AssetBundleFile.describe("背景のOSごとのAssetBundle URL"),
    priority: z.int().describe("表示優先度。数値が大きいほど先に表示する。"),
  })
)

export type SpaceBackgroundDocumentValue = z.infer<typeof SpaceBackgroundDocumentValue>

export const SpaceBackgroundDocumentBody = z.intersection(
  SpaceBackgroundDocumentValue,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type SpaceBackgroundDocumentBody = z.infer<typeof SpaceBackgroundDocumentBody>

export type SpaceBackgroundDocument = SpaceBackgroundDocumentBody & {
  id: SpaceBackgroundId
}

export class SpaceBackgroundDocuments {
  static toDocumentData(doc: SpaceBackgroundDocumentBody): object {
    return {
      ...doc,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SpaceBackgroundDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    const rawImages = data["images"] ?? { home: [] }
    const home: MediaImage[] = (rawImages.home ?? []).map((img: unknown) => {
      if (typeof img === "string") {
        // レガシー形式: gs:// URL 文字列 → MediaImage に変換
        return {
          kind: "image" as const,
          value: "",
          location: img,
          updated_at: 0,
        }
      }
      if (typeof img === "object" && img !== null && "kind" in img) {
        return img as MediaImage
      }
      // 想定外の形式: フォールバック
      return {
        kind: "image" as const,
        value: "",
        location: "",
        updated_at: 0,
      }
    })

    return {
      id: SpaceBackgroundId(doc.id),
      name: data["name"],
      atmosphere_text: data["atmosphere_text"],
      priority: data["priority"],
      capacity: data["capacity"],
      recommend_bgm: data["recommend_bgm"],
      thumbnail_url: data["thumbnail_url"],
      asset_bundle: data["asset_bundle"],
      images: { home },
      status: data["status"],
      can_delete: data["can_delete"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}
